import { combineReducers } from '@reduxjs/toolkit';
import _ from 'lodash';

import { advancementsKey } from '@modules/advancement/advancementsBulkEntry/contants';
import {
  ORG_ROSTER_ERROR,
  ORG_ROSTER_RESPONSE,
} from '@modules/advancement/common';
import {
  deleteSubUnitResponse,
  editSubUnitMembers,
  getSubUnitsResponse,
} from '@modules/advancement/subUnits/duck/actions';
import {
  getDeletedYouthPos,
  subUnitYouthDictionary,
} from '@modules/advancement/utilsTyped';
import { memberTypeLabels } from '@shared/constants';
import { SET_ORGANIZATION } from '@shared/duck/actions';
import { resetOnLogoutReducer } from '@utils';

import { DESELECT_PERSON } from '../../../progress/duck/actions';
import { UNASSIGNED } from '../constants';
import {
  PACK_ROSTER_ERROR,
  PACK_ROSTER_FILTER,
  PACK_ROSTER_REQUEST,
  PACK_ROSTER_RESPONSE,
  SAVE_OPTOUT,
  SAVE_OPTOUT_ERROR,
  SET_MEMBER_TYPE_SECTION_EXPANDED,
  SET_SELECTED_KEYS,
  SET_SHOW_MEMBER_TYPES,
  SET_SHOW_SUB_UNITS,
  SET_SUB_UNITS_SECTION_EXPANDED,
  SET_SUB_UNIT_USER_DATA,
} from './actions';
import { packRosterSkipped } from './actionsTyped';

const loading = (state = false, { type }) => {
  switch (type) {
    case PACK_ROSTER_REQUEST: {
      return true;
    }
    case PACK_ROSTER_RESPONSE:
    case PACK_ROSTER_ERROR:
    case packRosterSkipped.type: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const items = (state = [], { type, payload }) => {
  switch (type) {
    case deleteSubUnitResponse.type: {
      const { subUnitId, isDen } = payload;
      const modifiedRoster = [...state].reduce((curr, member) => {
        if (!member.positions) return [...curr, member];

        const newPositions = member.positions.reduce((currPos, pos) => {
          const belongsToSubUnit = [pos.patrolId, pos.denId].includes(
            subUnitId,
          );
          if (belongsToSubUnit) {
            return member.isAdult
              ? [...currPos]
              : [...currPos, getDeletedYouthPos(isDen)];
          }
          return [...currPos, pos];
        }, []);
        const reducedMember = {
          ...member,
          positions: newPositions,
        };
        return [...curr, reducedMember];
      }, []);
      return modifiedRoster;
    }

    case editSubUnitMembers.type: {
      const {
        subUnit,
        addedYouths,
        addedAdults,
        addedDenChiefs,
        deletedMembers,
        changedName,
      } = payload;

      const modifiedRoster = [...state].reduce((curr, member) => {
        const userId = member.userId;

        const foundDeletedMemberPos = deletedMembers?.find(
          el => el === userId || el.userId === userId,
        );
        if (foundDeletedMemberPos) {
          const reducedMember = {
            ...member,
            positions: member.positions.filter(
              pos => ![pos.patrolId, pos.denId].includes(subUnit.subUnitId),
            ),
          };
          return [...curr, reducedMember];
        }

        const addedMembers = [
          ...addedYouths,
          ...addedAdults,
          ...addedDenChiefs,
        ];
        const foundMemberPos = addedMembers.find(el => el.userId === userId);
        if (foundMemberPos) {
          // Youths now can be in multiple patrols (SCOUTS_BSA)
          const filteredPos = member.positions.filter(
            el =>
              !_.values(subUnitYouthDictionary).includes(el.positionId) ||
              el.positionId === subUnitYouthDictionary.SCOUTS_BSA,
          );
          const newPositions = [...filteredPos, foundMemberPos];
          const extendedMember = {
            ...member,
            positions: newPositions,
          };
          return [...curr, extendedMember];
        }
        if (changedName) {
          const newPositions = member?.positions?.map(pos => {
            if (subUnit.subUnitId === pos.patrolId) {
              return { ...pos, patrolName: subUnit.subUnitName };
            }

            if (subUnit.subUnitId === pos.denId) {
              return { ...pos, denNumber: subUnit.subUnitName };
            }
            return pos;
          });
          const modifiedMember = {
            ...member,
            positions: newPositions,
          };
          return [...curr, modifiedMember];
        }

        return [...curr, member];
      }, []);
      return modifiedRoster;
    }

    case PACK_ROSTER_RESPONSE: {
      return payload;
    }
    case PACK_ROSTER_ERROR:
    case SET_ORGANIZATION: {
      return [];
    }
    default: {
      return state;
    }
  }
};

const selectedKeys = (state = [], { type, payload }) => {
  switch (type) {
    case SET_SELECTED_KEYS: {
      return payload;
    }
    case DESELECT_PERSON: {
      return state.filter(id => id != payload);
    }
    case PACK_ROSTER_RESPONSE: {
      return payload
        .filter(({ memberId }) => state.includes(memberId))
        .map(({ memberId }) => memberId);
    }
    case PACK_ROSTER_ERROR:
    case PACK_ROSTER_FILTER:
    case SET_SHOW_SUB_UNITS:
    case SET_SHOW_MEMBER_TYPES: {
      return [];
    }
    default: {
      return state;
    }
  }
};

const defaultExpandedItemType = {
  [advancementsKey.RANKS]: true,
  [advancementsKey.MERIT_BADGES]: true,
  [advancementsKey.ADVENTURES]: true,
};

const subUnitsSectionExpanded = (state = {}, { type, payload }) => {
  switch (type) {
    case getSubUnitsResponse.type: {
      const subUnitsSectionsExpanded = payload?.subUnits?.reduce(
        (acc, subUnit) => ({ ...acc, [subUnit.subUnitId]: true }),
        {},
      );
      return { ...subUnitsSectionsExpanded, [UNASSIGNED]: true };
    }
    case PACK_ROSTER_RESPONSE: {
      const youthUserIds = payload.reduce((acc, youth) => {
        acc[youth.userId] = true;
        return acc;
      }, {});

      // Default Expanded for Group by Youth or Item Type
      return {
        ...state,
        ...youthUserIds,
        ...defaultExpandedItemType,
        [UNASSIGNED]: true,
      };
    }
    case SET_SUB_UNITS_SECTION_EXPANDED: {
      return { ...state, ...payload };
    }
    default: {
      return state;
    }
  }
};

const subUnitUserData = (
  state = { subUnitId: 0, subUnitName: '' },
  { type, payload },
) => {
  switch (type) {
    case SET_SUB_UNIT_USER_DATA: {
      return payload;
    }
    default: {
      return state;
    }
  }
};

const showMemberTypes = (state = false, { type, payload }) => {
  switch (type) {
    case SET_ORGANIZATION: {
      return false;
    }
    case SET_SHOW_MEMBER_TYPES: {
      return payload;
    }
    default: {
      return state;
    }
  }
};

const memberTypeSectionExpanded = (state = {}, { type, payload }) => {
  const defaultState = {
    [memberTypeLabels.adult]: true,
    [memberTypeLabels.youth]: true,
    [memberTypeLabels.participant]: true,
  };
  switch (type) {
    case SET_MEMBER_TYPE_SECTION_EXPANDED: {
      return { ...state, ...payload };
    }
    default: {
      return defaultState;
    }
  }
};

const renewalRosterInfo = (state = {}, { type, payload }) => {
  switch (type) {
    case ORG_ROSTER_RESPONSE:
      return payload;

    case ORG_ROSTER_ERROR:
    case SET_ORGANIZATION: {
      return {};
    }
    default: {
      return state;
    }
  }
};

const loadingRenewal = (state = false, { type }) => {
  switch (type) {
    case SAVE_OPTOUT:
    case PACK_ROSTER_REQUEST: {
      return true;
    }
    case ORG_ROSTER_RESPONSE:
    case SAVE_OPTOUT_ERROR:
    case ORG_ROSTER_ERROR:
    case packRosterSkipped.type: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const reducer = combineReducers({
  loading,
  items,
  selectedKeys,
  subUnitsSectionExpanded,
  subUnitUserData,
  showMemberTypes,
  memberTypeSectionExpanded,
  renewalRosterInfo,
  loadingRenewal,
});

export default resetOnLogoutReducer(reducer);
